import { Stack, Text, Textarea } from "@mantine/core";

import { useFeedbackDraftStore } from "../../store";
import classes from "../../styles.module.css";
import { type FreeTextCategory, feedbackUIMappingFreeText, getTemplateFields } from "../../types";
import { FeedbackRadioGroup } from "./FeedbackRadioGroup";

function getPlaceholder(category?: string) {
    const freeTextCategory = category as FreeTextCategory | undefined;
    const { placeholder } = feedbackUIMappingFreeText.default;
    return freeTextCategory ? placeholder[freeTextCategory] : placeholder["Make a suggestion"];
}

// Each field subscribes to its own slice of the draft so a keystroke only re-renders the field being typed in
function DescriptionField({ isLoading, placeholder }: { isLoading: boolean; placeholder: string }) {
    const description = useFeedbackDraftStore((state) => state.draft.description);

    const onChangeText = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { draft, setDraft } = useFeedbackDraftStore.getState();
        setDraft({
            ...draft,
            description: event.currentTarget.value,
        });
    };

    // Fixed height via `rows`, not `autosize`: autosize forces a synchronous layout on every render
    return (
        <Textarea
            classNames={{ input: classes.input }}
            value={description}
            rows={12}
            mt="lg"
            onChange={onChangeText}
            placeholder={placeholder}
            data-testid="feedback-textarea"
            disabled={isLoading}
        />
    );
}

function TemplateField({ field, isLoading }: { field: string; isLoading: boolean }) {
    const value = useFeedbackDraftStore((state) => state.draft.templateResponses?.[field] ?? "");

    const onChangeTemplateField = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { draft, setDraft } = useFeedbackDraftStore.getState();
        setDraft({
            ...draft,
            templateResponses: {
                ...draft.templateResponses,
                [field]: event.currentTarget.value,
            },
        });
    };

    return (
        <Stack gap="xxs">
            <Text size="sm" fw={600}>
                {field}
            </Text>
            <Textarea
                classNames={{ input: classes.input }}
                value={value}
                minRows={2}
                maxRows={4}
                autosize
                onChange={onChangeTemplateField}
                data-testid={`feedback-template-field-${field}`}
                disabled={isLoading}
            />
        </Stack>
    );
}

export function FreeTextFeedback({ isLoading, isTraining }: { isLoading: boolean; isTraining?: boolean }) {
    const category = useFeedbackDraftStore((state) => state.draft.category);
    const { subtitle, categories, bottomText } = isTraining
        ? feedbackUIMappingFreeText.expertUniversityDefault
        : feedbackUIMappingFreeText.default;

    const templateFields = getTemplateFields(category);

    return (
        <Stack>
            <Text size="sm" c="dark.3" mb="lg">
                {subtitle}
            </Text>
            <FeedbackRadioGroup categories={categories} direction="row" />
            {templateFields ? (
                <Stack mt="lg" gap="md">
                    {templateFields.map((field) => (
                        <TemplateField key={field} field={field} isLoading={isLoading} />
                    ))}
                </Stack>
            ) : (
                <DescriptionField isLoading={isLoading} placeholder={getPlaceholder(category)} />
            )}
            <Text size="xs" c="dark.3" mt="xs" mb="xs">
                {bottomText}
            </Text>
        </Stack>
    );
}
